import { ChangeEvent, CSSProperties, ReactNode, SVGProps } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import cns from 'classnames';
import { useTranslation } from 'next-i18next';
import { ZepApiStatusType } from '@zep/apis';
import { useDialog } from '@zep/dialog';
import { useUploadImages } from '../../../../services';
import { Loading } from '../../common';
import S from './UploadImagesGrid.module.scss';

/**
 * 에셋 업로드시 업로드할 파일들의 목록을 관리하는 컴포넌트
 */
const UploadImagesGrid = (props: AssetUploadGridProps) => {
  const {
    gridKey,
    assetImageKeyList,
    onFilesAdd,
    onFileRemove,
    disabled,
    imageType = 0,
    fileMaxCount = 100,
    aspectRatio = '160 / 106'
  } = props;
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['store']);
  const loadingSpinner = useModal(Loading);
  const suspendFileUpload = (fileInput: HTMLInputElement, message: string | ReactNode) => {
    fileInput.value = '';
    dialog.alert({
      title: message
    });
    return false;
  };
  const imageUploadMutation = useUploadImages(imageType, {
    onSuccess: data => {
      if (data.status === 0) {
        onFilesAdd(data.keys);
      }
    },
    onError: error => {
      console.error(error);
      if (error?.status === ZepApiStatusType.IS_NOT_IMAGE_FILE) {
        dialog.alert({
          title: t('store:store_alert_wrong_image_desc')
        });
      } else if (error?.status === ZepApiStatusType.SIZE_LIMIT_EXCEEDED) {
        dialog.alert(
        // @ts-ignore
        t('store:store_image_size_limit', {
          size: '400KB'
        }));
      } else {
        dialog.alert({
          title: error.message ||
          // @ts-ignore
          t('common:error.api.unknown')
        });
      }
    }
  });

  /**
   * 파일이 추가될때마다 파일 목록에 이미지와 함께 정보를 상위 컴포넌트로 전송
   * @param event 파일 Change 이벤트
   */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      if (assetImageKeyList.length + event.target.files?.length > fileMaxCount) {
        return suspendFileUpload(event.target, t('store:store_alert_max_registration_num', {
          count: fileMaxCount
        }));
      }
      const isValidEveryFiles = Array.from(event.target.files).every(uploadFile => {
        if (uploadFile.type !== 'image/png' && uploadFile.type !== 'image/jpeg' && uploadFile.type !== 'image/webp') {
          return suspendFileUpload(event.target, t('store:store_alert_wrong_image_desc'));
        } else if (uploadFile.size > 1024 * 400) {
          // 개별 파일 용량인 400KB를 초과하는 경우 중단
          return suspendFileUpload(event.target, t('store:store_image_size_limit', {
            size: '400KB'
          }));
        } else if (uploadFile.name.length > 100) {
          // 업로드할 파일 이름의 길이를 100글자로 제한
          return suspendFileUpload(event.target, t('store:image_max_name_length'));
        }
        return true;
      });
      if (isValidEveryFiles) {
        // @ts-ignore
        loadingSpinner.show(t('store:spinnerMessage.upload.image'));
        imageUploadMutation.mutate(event.target.files, {
          onSettled: () => {
            event.target.value = '';
          }
        });
      }
    }
  };
  return <div className={S.upload_grid} style={{
    '--asset-aspeat-ratio': aspectRatio
  } as CSSProperties} data-sentry-component="UploadImagesGrid" data-sentry-source-file="UploadImagesGrid.tsx">
      <input type="file" id={`upload-asset-${gridKey}-input`} multiple onChange={handleFileChange} accept=".jpg, .jpeg, .png, .webp" disabled={disabled} />
      <label className={cns(S.upload_asset_input, {
      [S.disabled]: disabled
    })} htmlFor={`upload-asset-${gridKey}-input`}>
        <PlugIcon aria-label="Plus" data-sentry-element="PlugIcon" data-sentry-source-file="UploadImagesGrid.tsx" />
        <div className={S.add_text}>{t('store:store_add_image')}</div>
      </label>
      {assetImageKeyList.map?.((imageKey, index) => <div key={`${imageKey}${index}`} className={S.grid_item_wrapper}>
          <div className={S.grid_item_image}>
            <img src={`${process.env.NEXT_PUBLIC_STORAGE_URL}/${imageKey}`} alt={imageKey} />
          </div>
          {!disabled && <button type="button" className={S.grid_item_delete_button} onClick={() => !disabled && onFileRemove(index)}>
              <TrashIcon aria-label="Delete" />
            </button>}
        </div>)}
    </div>;
};
export default UploadImagesGrid;
type AssetUploadGridProps = {
  gridKey: string;
  assetImageKeyList: string[];
  onFileRemove: (index: number) => void;
  onFilesAdd: (imageKeys: string[]) => void;
  disabled?: boolean;
  imageType?: number;
  fileMaxCount?: number;
  aspectRatio?: string;
};
const TrashIcon = (props: SVGProps<SVGSVGElement>) => <svg width={16} height={16} fill="none" xmlns="http://www.w3.org/2000/svg" {...props} data-sentry-element="svg" data-sentry-component="TrashIcon" data-sentry-source-file="UploadImagesGrid.tsx">
    <path d="M4.667 2.667c0-.737.597-1.334 1.334-1.334h4c.736 0 1.333.597 1.333 1.334V4h1.326a.64.64 0 0 1 .013 0H14a.667.667 0 0 1 0 1.333h-.713l-.578 8.095a1.333 1.333 0 0 1-1.33 1.239H4.62c-.7 0-1.28-.54-1.33-1.239l-.578-8.095h-.712A.667.667 0 1 1 2 4h1.327a.64.64 0 0 1 .013 0h1.326V2.667ZM6.001 4h4V2.667H6V4ZM4.05 5.333l.571 8h6.759l.571-8H4.05Zm2.617 1.334c.369 0 .667.298.667.666v4a.667.667 0 1 1-1.333 0v-4c0-.368.298-.666.666-.666Zm2.667 0c.368 0 .667.298.667.666v4a.667.667 0 0 1-1.334 0v-4c0-.368.299-.666.667-.666Z" fill="currentCOlor" data-sentry-element="path" data-sentry-source-file="UploadImagesGrid.tsx" />
  </svg>;
const PlugIcon = (props: SVGProps<SVGSVGElement>) => <svg width={50} height={50} fill="none" xmlns="http://www.w3.org/2000/svg" {...props} data-sentry-element="svg" data-sentry-component="PlugIcon" data-sentry-source-file="UploadImagesGrid.tsx">
    <path d="M25.5 8.333c1.151 0 2.084.933 2.084 2.084v12.5h12.5a2.083 2.083 0 1 1 0 4.166h-12.5v12.5a2.083 2.083 0 0 1-4.167 0v-12.5h-12.5a2.083 2.083 0 1 1 0-4.166h12.5v-12.5c0-1.151.933-2.084 2.084-2.084Z" fill="currentColor" opacity={0.3} data-sentry-element="path" data-sentry-source-file="UploadImagesGrid.tsx" />
  </svg>;